import React, { useState } from 'react'
import { X, Shield, Key, AlertTriangle, Download, Copy, RefreshCw } from 'lucide-react'
import authAPI from '../../lib/authAPI'
import toast from 'react-hot-toast'

const MfaManageModal = ({ mfaStatus, onClose, onMfaDisabled, onRefresh }) => {
  const [showDisableConfirm, setShowDisableConfirm] = useState(false)
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false)
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Mock recovery codes - in real implementation, these would come from backend
  const mockRecoveryCodes = [
    'ABC123', 'DEF456', 'GHI789', 'JKL012',
    'MNO345', 'PQR678', 'STU901', 'VWX234'
  ]

  const handleDisableMfa = async (e) => {
    e.preventDefault()
    if (!password.trim()) return

    setLoading(true)
    setError('')

    try {
      // In a real implementation, you'd verify the password first
      const { error } = await authAPI.disableTotp()
      if (error) {
        setError(error.message)
        return
      }

      onMfaDisabled()
    } catch (err) {
      setError('Failed to disable MFA. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(mockRecoveryCodes.join('\n'))
    toast.success('Recovery codes copied to clipboard')
  }

  const downloadRecoveryCodes = () => {
    const content = `E8 Tracker - Two-Factor Authentication Recovery Codes

Generated: ${new Date().toLocaleDateString()}

${mockRecoveryCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

Important:
- Save these codes in a secure location
- Each code can only be used once
- Use these codes if you lose access to your authenticator app
- Generate new codes if you suspect these have been compromised`

    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'e8-tracker-recovery-codes.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success('Recovery codes downloaded')
  }

  const enabledDate = mfaStatus?.profile?.mfa_enabled_at

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
        
        <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Two-factor authentication
                </h3>
                <p className="text-sm text-gray-500">
                  Manage your account security settings
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Status */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Shield className="w-5 h-5 text-green-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Two-factor authentication is enabled
                </p>
                {enabledDate && (
                  <p className="text-xs text-green-600 mt-1">
                    Enabled on {new Date(enabledDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>

          {!showDisableConfirm && !showRecoveryCodes && (
            <div className="space-y-4">
              {/* Current Setup */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Current setup</h4>
                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <Key className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Authenticator app</p>
                    <p className="text-xs text-gray-500">TOTP-based authentication</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Recovery options</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => setShowRecoveryCodes(true)}
                    className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center">
                      <Key className="w-4 h-4 text-gray-400 mr-3" />
                      <span className="text-sm text-gray-900">View recovery codes</span>
                    </div>
                    <span className="text-xs text-gray-500">→</span>
                  </button>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Danger zone</h4>
                <button
                  onClick={() => setShowDisableConfirm(true)}
                  className="w-full flex items-center justify-center px-4 py-2 border border-red-300 text-sm font-medium text-red-700 bg-white rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Disable two-factor authentication
                </button>
              </div>
            </div>
          )}

          {/* Recovery Codes View */}
          {showRecoveryCodes && (
            <div>
              <button
                onClick={() => setShowRecoveryCodes(false)}
                className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                ← Back to settings
              </button>
              
              <h4 className="text-base font-medium text-gray-900 mb-2">
                Recovery codes
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Use these codes to access your account if you lose your authenticator device. Each code can only be used once.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {mockRecoveryCodes.map((code, index) => (
                    <div key={index} className="flex items-center">
                      <span className="text-gray-500 w-4">{index + 1}.</span>
                      <span className="ml-2">{code}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex space-x-3 mb-4">
                <button
                  onClick={copyRecoveryCodes}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy codes
                </button>
                <button
                  onClick={downloadRecoveryCodes}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mr-2 mt-0.5" />
                  <p className="text-xs text-yellow-800">
                    Store these codes in a secure location. If you suspect they've been compromised, generate new ones immediately.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Disable Confirmation */}
          {showDisableConfirm && (
            <div>
              <button
                onClick={() => setShowDisableConfirm(false)}
                className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
              >
                ← Back to settings
              </button>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex">
                  <AlertTriangle className="w-5 h-5 text-red-600 mr-3 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800 mb-1">
                      Disable two-factor authentication?
                    </h4>
                    <p className="text-sm text-red-700">
                      This will make your account less secure. You'll only need your password to sign in.
                    </p>
                  </div>
                </div>
              </div>
              
              <form onSubmit={handleDisableMfa}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm your password to continue
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your password"
                    required
                  />
                  {error && (
                    <p className="mt-2 text-sm text-red-600">{error}</p>
                  )}
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowDisableConfirm(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !password.trim()}
                    className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Disabling...' : 'Disable MFA'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MfaManageModal
